/**
 * 限流控制器
 * 用于防止暴力破解攻击
 */

class RateLimiter {
    constructor() {
        this.attempts = new Map();
        this.MAX_ATTEMPTS = 5;           // 最大尝试次数
        this.WINDOW_MS = 15 * 60 * 1000; // 时间窗口15分钟
        this.BLOCK_DURATION = 30 * 60 * 1000; // 锁定时长30分钟
    }

    /**
     * 检查是否允许登录尝试
     * @param {string} identifier - 用户标识符（如用户名、IP等）
     * @returns {Object} 检查结果
     */
    check(identifier) {
        const now = Date.now();
        const record = this.attempts.get(identifier);

        if (!record) {
            return {allowed: true};
        }

        // 检查是否在锁定期内
        if (record.lockedUntil && now < record.lockedUntil) {
            const retryAfter = Math.ceil((record.lockedUntil - now) / 1000);
            return {
                allowed: false,
                retryAfter,
                lockedUntil: record.lockedUntil
            };
        }

        // 检查时间窗口内的尝试次数
        const recentAttempts = record.attempts.filter(
            attempt => now - attempt.timestamp < this.WINDOW_MS
        );

        if (recentAttempts.length >= this.MAX_ATTEMPTS) {
            // 锁定账户
            record.lockedUntil = now + this.BLOCK_DURATION;
            this.saveToStorage();

            return {
                allowed: false,
                retryAfter: Math.ceil(this.BLOCK_DURATION / 1000),
                lockedUntil: record.lockedUntil
            };
        }

        return {allowed: true};
    }

    /**
     * 记录失败尝试
     * @param {string} identifier - 用户标识符
     */
    recordFailure(identifier) {
        const now = Date.now();
        let record = this.attempts.get(identifier);

        if (!record) {
            record = {
                attempts: [],
                lockedUntil: null
            };
        }

        record.attempts.push({timestamp: now});

        // 保持最多10条记录
        if (record.attempts.length > 10) {
            record.attempts = record.attempts.slice(-10);
        }

        this.attempts.set(identifier, record);
        this.saveToStorage();
    }

    /**
     * 重置失败计数
     * @param {string} identifier - 用户标识符
     */
    reset(identifier) {
        this.attempts.delete(identifier);
        this.saveToStorage();
    }

    /**
     * 清理过期记录
     */
    cleanup() {
        const now = Date.now();
        for (const [identifier, record] of this.attempts.entries()) {
            // 清理过期的尝试记录
            record.attempts = record.attempts.filter(
                attempt => now - attempt.timestamp < this.WINDOW_MS
            );

            // 如果锁定期已过，清除锁定
            if (record.lockedUntil && now >= record.lockedUntil) {
                record.lockedUntil = null;
            }

            // 如果没有尝试记录且未锁定，删除整个记录
            if (record.attempts.length === 0 && !record.lockedUntil) {
                this.attempts.delete(identifier);
            }
        }
        this.saveToStorage();
    }

    /**
     * 保存到localStorage
     */
    saveToStorage() {
        try {
            const data = {
                attempts: Array.from(this.attempts.entries()),
                timestamp: Date.now()
            };
            localStorage.setItem('rate_limiter', JSON.stringify(data));
        } catch (error) {
            console.error('Failed to save rate limiter state:', error);
        }
    }

    /**
     * 从localStorage加载
     */
    loadFromStorage() {
        try {
            const data = JSON.parse(localStorage.getItem('rate_limiter') || '{}');
            if (data.attempts && Array.isArray(data.attempts)) {
                this.attempts = new Map(data.attempts);
                // 清理过期数据
                this.cleanup();
            }
        } catch (error) {
            console.error('Failed to load rate limiter state:', error);
        }
    }

    /**
     * 获取剩余尝试次数
     * @param {string} identifier - 用户标识符
     * @returns {number} 剩余尝试次数
     */
    getRemainingAttempts(identifier) {
        const record = this.attempts.get(identifier);
        if (!record) {
            return this.MAX_ATTEMPTS;
        }

        const now = Date.now();
        const recentAttempts = record.attempts.filter(
            attempt => now - attempt.timestamp < this.WINDOW_MS
        );

        return Math.max(0, this.MAX_ATTEMPTS - recentAttempts.length);
    }
}

// 导出单例实例
export default new RateLimiter();